import PropTypes from 'prop-types';
import { Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import NoDataCell from 'src/components/NoDataCell';
import StatusChip from 'src/components/StatusChip';
import TableContainer from 'src/components/Table/TableContainer';
import LoadingRows from 'src/components/Table/LoadingRows';

const TABLE_SX = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': { fontSize: '0.8125rem' },
};

const COLUMNS = [
  { key: 'name', label: 'Ring Name', width: '40%' },
  { key: 'deferralDays', label: 'Deferral Days', width: '20%' },
  { key: 'deviceCount', label: 'Devices', width: '20%' },
  { key: 'status', label: 'Status', width: '20%' },
];

const MUTED_COLOR = '#9CA3AF';
const ACTIVE_COLOR = '#7DFF92';
const INACTIVE_COLOR = '#DE312B';

// Rings arrive ordered by the backend (deferral days, then id), so this renders what it's given -
// that order is the deployment order. A row opens the ring's details page.
const DeploymentRingsTable = ({ rings, isLoading, onOpen }) => (
  <TableContainer roundedTop>
    <Table size="small" sx={TABLE_SX}>
      <colgroup>
        {COLUMNS.map(column => (
          <col key={column.key} style={{ width: column.width }} />
        ))}
      </colgroup>
      <TableHead sx={{ backgroundColor: '#3F3F3F' }}>
        <TableRow>
          {COLUMNS.map(column => (
            <TableCell key={column.key} sx={{ p: '8px 16px' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#DDDDDD' }}>
                {column.label}
              </Typography>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
        {isLoading ? (
          <LoadingRows columnCount={COLUMNS.length} rowCount={4} />
        ) : (
          <>
            {rings.map(ring => {
              const textColor = ring.isActive ? undefined : MUTED_COLOR;

              return (
                <TableRow
                  key={ring.id}
                  hover
                  onClick={() => onOpen(ring)}
                  sx={{ cursor: 'pointer' }}>
                  <TableCell sx={{ py: 0.75 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: textColor }}>
                        {ring.name}
                      </Typography>
                      {ring.isDefault ? (
                        <StatusChip
                          label="Default"
                          color="#87C8E0"
                          backgroundColor="#87C8E01F"
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ py: 0.75 }}>
                    <Typography variant="body2" sx={{ color: textColor }}>
                      {ring.deferralDays}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.75 }}>
                    <Typography variant="body2" sx={{ color: textColor }}>
                      {ring.deviceCount}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.75 }}>
                    <StatusChip
                      label={ring.isActive ? 'Active' : 'Inactive'}
                      color={ring.isActive ? ACTIVE_COLOR : INACTIVE_COLOR}
                      backgroundColor={`${ring.isActive ? ACTIVE_COLOR : INACTIVE_COLOR}1F`}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            {!rings.length ? (
              <NoDataCell colSpan={COLUMNS.length}>No deployment rings yet.</NoDataCell>
            ) : null}
          </>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

DeploymentRingsTable.propTypes = {
  rings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      deferralDays: PropTypes.number,
      deviceCount: PropTypes.number,
      isDefault: PropTypes.bool,
      isActive: PropTypes.bool,
    }),
  ).isRequired,
  isLoading: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
};

export default DeploymentRingsTable;
